import { CheckCircle2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { AttentionItem } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* "Needs a decision today". Approval requests are a first-class inbox, not a
 * notification that scrolls away: the rate freeze is the founders' main control
 * over margin, so a pending request appears here and STAYS here until resolved
 * (EXPERIENCE.md § Permission-Shaped UI, FR-36). */

/* Tone only; the label comes from the `dashboard.attention.kind` catalogue at
 * render, keyed by the item's kind. */
const KIND_TONES: Record<
  AttentionItem['kind'],
  'brand' | 'attention' | 'negative' | 'neutral'
> = {
  stuck: 'attention',
  minus: 'negative',
  approval: 'attention',
  overdue: 'negative',
  checklist: 'attention',
  'quote-followup': 'brand',
}

export function AttentionList({
  items,
  stuckDays,
}: {
  items: AttentionItem[]
  /** The threshold the server computed with, so the empty state can say what
   *  "stuck" means rather than the client hard-coding a number the server
   *  owns (FR-52). */
  stuckDays: number
}) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.attention.title')}</CardTitle>
      </CardHeader>

      {items.length === 0 ? (
        /* Good news stated plainly, not an empty box (UX-DR-17). The count is
           named so "nothing here" reads as an answer to a question rather than
           as a screen that failed to load. */
        <EmptyState
          icon={CheckCircle2}
          title={t('dashboard.attention.emptyTitle')}
          /* `count`, not `days`: i18next selects the plural form from that
             name specifically, and any other name silently returns the raw
             key — which is what a user would then read. */
          description={t('dashboard.attention.emptyDescription', {
            count: stuckDays,
          })}
        />
      ) : (
        <ul className="mt-3 flex flex-col">
          {items.map((item, index) => {
            const tone = KIND_TONES[item.kind]
            return (
              <li key={item.id}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-[18px] py-2.5 no-underline',
                    'hover:bg-surface-hover transition-colors',
                    'duration-[var(--dur-instant)] ease-[var(--ease-standard)]',
                    'hover:no-underline',
                    index !== items.length - 1 && 'border-line-hair border-b',
                  )}
                >
                  {/* min-w, not w: the pill holds a TRANSLATED label, and
                      Russian runs 15–30% longer than English (UX-DR-30). A
                      fixed width truncates «Согласование» to fit a box sized
                      for "Approval". The floor keeps the column aligned when
                      the labels are short. */}
                  <span className="min-w-[92px] shrink-0">
                    <StatusPill tone={tone}>{t(`dashboard.attention.kind.${item.kind}`)}</StatusPill>
                  </span>

                  {/* Record identifiers take emphasis weight in brand — one of
                      the four sanctioned uses of weight 600. */}
                  <span className="text-brand w-[104px] shrink-0 truncate text-[13px] font-semibold">
                    {item.recordNumber}
                  </span>

                  <span className="flex min-w-0 flex-1 flex-col">
                    {/* Two kinds carry a machine TOKEN and have their sentence
                        composed here, because the server sending "No movement
                        for 4 days" would be an English string on a Russian
                        screen — and Russian needs three plural forms for that
                        noun (FR-2).

                        `stuck` carries the bare day count. `minus` carries which
                        FIGURE is negative — `forecast` on a case still running,
                        `final` on one that closed at a loss — because the two
                        mean different things to a founder: a forecast is still
                        recoverable and a final figure is not. Every other kind
                        carries text of its own. */}
                    <span className="text-text-heading truncate text-[13px] leading-[1.45] font-normal">
                      {item.kind === 'stuck'
                        ? t('dashboard.attention.stuckFor', {
                            count: Number(item.title),
                          })
                        : item.kind === 'minus'
                          ? t(`dashboard.attention.minusBasis.${item.title}`, {
                              defaultValue: item.title,
                            })
                          : item.title}
                    </span>
                    <span className="text-text-muted truncate text-[12px] leading-[1.4] font-light">
                      {item.detail}
                    </span>
                  </span>

                  {item.amount ? (
                    <span
                      className={cn(
                        'shrink-0 text-[13px] whitespace-nowrap',
                        item.amount.amount.startsWith('-')
                          ? 'text-negative font-semibold'
                          : 'text-text-heading',
                      )}
                    >
                      {/* Only the digits are tabular. Letting the currency code
                          inherit it pads the space before "USD" to a digit's
                          width and detaches the code from its figure. */}
                      <span className="asa-num">
                        {formatNumber(item.amount.amount, 2)}
                      </span>
                      <span className="text-text-muted ml-1 font-normal">
                        {item.amount.currency}
                      </span>
                    </span>
                  ) : null}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
